/**
 * Auth API Hooks
 *
 * TanStack Query hooks for authentication and API keys.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCurrentUserApiV1AuthMeGet,
  listApiKeysApiV1AuthApiKeysGet,
  createApiKeyApiV1AuthApiKeysPost,
  revokeApiKeyApiV1AuthApiKeysKeyIdDelete,
} from '@/api';
import type { ApiKeyCreate } from '@/api';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
  apiKeys: () => [...authKeys.all, 'apiKeys'] as const,
};

/**
 * Get current authenticated user.
 */
export function useCurrentUser() {
  const { enabled: authEnabled } = useAuthEnabled();

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const response = await getCurrentUserApiV1AuthMeGet();
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch user'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * List all API keys for the tenant.
 */
export function useApiKeys() {
  const { enabled: authEnabled } = useAuthEnabled();

  return useQuery({
    queryKey: authKeys.apiKeys(),
    queryFn: async () => {
      const response = await listApiKeysApiV1AuthApiKeysGet();
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch API keys'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Create a new API key.
 */
export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiKeyCreate) => {
      const response = await createApiKeyApiV1AuthApiKeysPost({
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create API key'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.apiKeys() });
    },
  });
}

/**
 * Revoke an API key.
 */
export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyId: string) => {
      const response = await revokeApiKeyApiV1AuthApiKeysKeyIdDelete({
        path: { key_id: keyId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to revoke API key'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.apiKeys() });
    },
  });
}
